/**
 * Chart of Accounts Form
 *
 * Create and edit chart of accounts entries.
 */

import React, { useState } from 'react';
import {
  FormField,
  FormRow,
  FormSection,
  FormActions,
  TextInput,
  TextArea,
  Select,
} from './FormComponents';
import { chartOfAccounts } from '@business-assistant/shared/data/accounting';
import type { ChartOfAccountsEntry, AccountCategory } from '@business-assistant/shared/data/types';

// =============================================================================
// TYPES
// =============================================================================

interface ChartOfAccountsFormData {
  code: string;
  name: string;
  category: AccountCategory;
  parentId: string | null;
  description: string;
  isLeaf: boolean;
}

interface ChartOfAccountsFormProps {
  entry?: ChartOfAccountsEntry;
  onSave: (data: ChartOfAccountsFormData) => void;
  onCancel: () => void;
}

// =============================================================================
// CONSTANTS
// =============================================================================

const CATEGORY_OPTIONS = [
  { value: 'assets', label: 'Attività' },
  { value: 'liabilities', label: 'Passività' },
  { value: 'equity', label: 'Patrimonio Netto' },
  { value: 'revenue', label: 'Ricavi' },
  { value: 'expenses', label: 'Costi' },
];

const CATEGORY_COLORS: Record<AccountCategory, { bg: string; text: string }> = {
  assets: { bg: '#dbeafe', text: '#3b82f6' },
  liabilities: { bg: '#fef3c7', text: '#f59e0b' },
  equity: { bg: '#f3e8ff', text: '#8b5cf6' },
  revenue: { bg: '#dcfce7', text: '#22c55e' },
  expenses: { bg: '#fee2e2', text: '#ef4444' },
};

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export function ChartOfAccountsForm({ entry, onSave, onCancel }: ChartOfAccountsFormProps) {
  const isEditing = !!entry;

  const [formData, setFormData] = useState<ChartOfAccountsFormData>({
    code: entry?.code || '',
    name: entry?.name || '',
    category: entry?.category || 'expenses',
    parentId: entry?.parentId || null,
    description: entry?.description || '',
    isLeaf: entry?.isLeaf ?? true,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Parent options (non-leaf accounts of same category)
  const parentOptions = chartOfAccounts
    .filter((a) => !a.isLeaf && a.category === formData.category && a.id !== entry?.id)
    .map((a) => ({
      value: a.id,
      label: `${a.code} - ${a.name}`,
    }));

  // Handlers
  const updateField = <K extends keyof ChartOfAccountsFormData>(
    field: K,
    value: ChartOfAccountsFormData[K]
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const handleCategoryChange = (category: AccountCategory) => {
    updateField('category', category);
    // Reset parent if category changes
    updateField('parentId', null);
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.code.trim()) {
      newErrors.code = 'Codice obbligatorio';
    } else if (!/^\d+$/.test(formData.code)) {
      newErrors.code = 'Il codice deve contenere solo numeri';
    } else {
      // Check for duplicate code (excluding current entry if editing)
      const duplicate = chartOfAccounts.find(
        (a) => a.code === formData.code && a.id !== entry?.id
      );
      if (duplicate) {
        newErrors.code = 'Codice già esistente';
      }
    }

    if (!formData.name.trim()) {
      newErrors.name = 'Nome obbligatorio';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validate()) {
      onSave(formData);
    }
  };

  const categoryColor = CATEGORY_COLORS[formData.category];

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '1rem',
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div
        style={{
          background: 'var(--bg, white)',
          borderRadius: '0.75rem',
          width: '100%',
          maxWidth: '500px',
          maxHeight: '90vh',
          overflow: 'auto',
          boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '1rem 1.5rem',
            borderBottom: '1px solid var(--border, #e5e7eb)',
          }}
        >
          <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 600 }}>
            {isEditing ? 'Modifica Conto' : 'Nuovo Conto'}
          </h2>
          <button
            onClick={onCancel}
            style={{
              width: '32px',
              height: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'transparent',
              border: 'none',
              borderRadius: '0.375rem',
              cursor: 'pointer',
            }}
          >
            <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: '1.5rem' }}>
          <FormSection title="Categoria">
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
              {CATEGORY_OPTIONS.map((cat) => {
                const colors = CATEGORY_COLORS[cat.value as AccountCategory];
                const isSelected = formData.category === cat.value;
                return (
                  <button
                    key={cat.value}
                    type="button"
                    onClick={() => handleCategoryChange(cat.value as AccountCategory)}
                    style={{
                      padding: '0.5rem 1rem',
                      background: isSelected ? colors.bg : 'var(--bg-secondary, #f9fafb)',
                      border: `2px solid ${isSelected ? colors.text : 'transparent'}`,
                      borderRadius: '0.5rem',
                      fontSize: '0.8125rem',
                      fontWeight: 500,
                      color: isSelected ? colors.text : 'var(--text, #111827)',
                      cursor: 'pointer',
                      transition: 'all 0.15s',
                    }}
                  >
                    {cat.label}
                  </button>
                );
              })}
            </div>
          </FormSection>

          <FormSection title="Dati Conto">
            <FormRow>
              <FormField label="Codice" required error={errors.code}>
                <TextInput
                  value={formData.code}
                  onChange={(v) => updateField('code', v)}
                  placeholder="1000"
                />
              </FormField>
              <FormField label="Tipo">
                <div style={{ display: 'flex', gap: '0.75rem', paddingTop: '0.5rem' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      checked={formData.isLeaf}
                      onChange={() => updateField('isLeaf', true)}
                    />
                    <span style={{ fontSize: '0.875rem' }}>Conto (usabile)</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      checked={!formData.isLeaf}
                      onChange={() => updateField('isLeaf', false)}
                    />
                    <span style={{ fontSize: '0.875rem' }}>Gruppo</span>
                  </label>
                </div>
              </FormField>
            </FormRow>

            <FormField label="Nome" required error={errors.name}>
              <TextInput
                value={formData.name}
                onChange={(v) => updateField('name', v)}
                placeholder="Nome del conto"
              />
            </FormField>

            {parentOptions.length > 0 && (
              <FormField label="Conto Padre">
                <Select
                  value={formData.parentId || ''}
                  onChange={(v) => updateField('parentId', v || null)}
                  options={[{ value: '', label: 'Nessuno (conto principale)' }, ...parentOptions]}
                />
              </FormField>
            )}

            <FormField label="Descrizione">
              <TextArea
                value={formData.description}
                onChange={(v) => updateField('description', v)}
                placeholder="Descrizione opzionale..."
                rows={2}
              />
            </FormField>
          </FormSection>

          {/* Preview */}
          <div
            style={{
              padding: '1rem',
              background: categoryColor.bg,
              borderRadius: '0.5rem',
              marginBottom: '1rem',
            }}
          >
            <div style={{ fontSize: '0.75rem', color: categoryColor.text, marginBottom: '0.25rem' }}>
              Anteprima
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>
                {formData.code || '----'}
              </span>
              <span style={{ fontWeight: 500 }}>
                {formData.name || 'Nome conto'}
              </span>
              {!formData.isLeaf && (
                <span
                  style={{
                    fontSize: '0.625rem',
                    padding: '0.125rem 0.375rem',
                    background: 'rgba(0,0,0,0.1)',
                    borderRadius: '9999px',
                  }}
                >
                  Gruppo
                </span>
              )}
            </div>
          </div>

          <FormActions
            onCancel={onCancel}
            onSubmit={handleSubmit}
            submitLabel={isEditing ? 'Salva Modifiche' : 'Crea Conto'}
          />
        </div>
      </div>
    </div>
  );
}

export type { ChartOfAccountsFormData };
